import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import { useMe } from '@/hooks/use-me';

import { FiltersSchema, type SelectedFilters } from '../entities/filters';

import { mockedFilters } from './__mocked_data__/filters';

export namespace GetFiltersQuery {
  export const key = (input: Input) => [
    'warranty-v3',
    'filters',
    input.systemId,
    input.selectedFilters,
  ];

  export type Input = {
    systemId: string;
    selectedFilters: SelectedFilters;
  };
  export const OutputSchema = z.object({
    filters: FiltersSchema,
  });
  export type Output = z.infer<typeof OutputSchema>;

  function outputMapper(
    result: FeApi.ListWarrantyFiltersViewOutput,
  ): z.infer<typeof OutputSchema> {
    const filters = Object.fromEntries(
      Object.entries(result.filters)
        .filter(([, filter]) => filter && filter.values)
        .map(([key, filter]) => [
          key,
          {
            id: filter.id,
            label: filter.label,
            values: (filter.values || []).map((value: any) => ({
              id: value.id || value.value || String(value),
              label: value.label || value.name || String(value),
            })),
          },
        ]),
    );

    return {
      filters,
    };
  }

  function useQueryFn() {
    const me = useMe();
    const filtersRetrieve = useInjectFeApiContext(
      FeApi.warrantyV2FiltersRetrieve,
    );

    return async function queryFn(input: Input): Promise<Output> {
      if (me.config.id === '49') {
        return mockedFilters(input);
      }

      const result = await filtersRetrieve({
        ...input.selectedFilters,
        systemId: input.systemId,
      });

      if (result.status !== 200) {
        throw new Error('Failed to fetch filters');
      }

      const output = await OutputSchema.safeParseAsync(
        outputMapper(result.data),
      );

      if (!output.success) {
        throw new Error('Failed to parse fe_api filters data', {
          cause: output.error,
        });
      }

      return output.data;
    };
  }

  export function useQuery(input: Input) {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      queryKey: key(input),
      queryFn: () => queryFn(input),
      keepPreviousData: true,
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
